<script setup lang="ts">
import { useRoute } from 'vue-router'
import {
  getAdList,
  getFloorDetailIndex,
  getFloorDetailIndexRoundFloor,
  getFloorDetails,
  getFloorMayLike,
  getFloorTabSwitch,
  saveBuryingPoint,
} from '~/server/newhouse'
import { useCityInfoStore } from '~/stores/useCityInfo'
import { ResultEnum } from '~/enums/interface'
import type { TabDataType } from '~/server/newhouse/interface'
import { getSignUpConfig } from '~/server/common'
import { useRongIMChat } from '~/composables/useRongIMChat'

const { city } = useCityInfoStore()
const { initRongIMChatMsg } = useRongIMChat()

const route = useRoute()

/**
 * 猜你喜欢数据获取
 */
const { data: floorMayLikeData } = await useAsyncData(() => getFloorMayLike({ cityId: city, current: 1, pageSize: 5 }), {
  transform(res) {
    const { code, dataBody = [] as any[] } = res
    return code === 0 ? dataBody.list : []
  },
})

/**
 * 周边楼盘
 */
const { data: roundData } = await useAsyncData(() => getFloorDetailIndexRoundFloor({ cityId: city, floorId: route.params.floorId }), {
  transform(res) {
    const { code, dataBody = [] as any[] } = res
    return code === 0 ? dataBody : []
  },
})

// TDK所需参数
const paramsRef = computed(() => {
  return {
    cityId: city,
    floorId: route.params.floorId,
    floorNewsTab: route?.params?.floorNewsTab ?? '', // 楼盘动态标签参数 0：全部
    houseModelId: route?.params?.houseModelId ?? '', // 户型ID
    rankingTypeId: route?.params?.rankingTypeId ?? '', // 榜单类型ID
    themeId: route?.params?.themeId ?? '', // 主题找房-主题ID入参
  }
})
// 获取TDK数据
const { tdkData, refresh } = await useMeta(paramsRef)
// 设置页面TDK参数
function setTDK() {
  useHead({
    title: tdkData.value?.title ?? '房产超市',
    meta: [
      { name: 'keywords', content: tdkData.value?.keywords ?? '' },
      { name: 'description', content: tdkData.value?.description ?? '' },
    ],
  })
}
setTDK()
onMounted(() => {
  initRongIMChatMsg()
})
// 监听路由切换
watch(() => route.fullPath, async () => {
  await refresh()
  setTDK()
  initRongIMChatMsg()
})
/**
 * 获取楼栋信息
 */
const { data: floorInfo, refresh: floorInfoRefresh } = await useAsyncData(() => getFloorDetails({ cityId: city, floorId: route.params.floorId }), {
  transform(res) {
    const { code, dataBody = {} as any } = res
    // return code === 0 ? dataBody : {}
    return code === ResultEnum.ERRORNOTFOUND ? 404 : dataBody
  },
})

const { data: floorInfoTwo }: any = await useAsyncData(() => getFloorDetailIndex({ cityId: city, floorId: route.params.floorId }), {
  transform(res) {
    saveBuryingPoint({
      floorId: route.params.floorId,
      pointTypeId: 1,
    })
    return res.code === ResultEnum.ERRORNOTFOUND ? 404 : res.dataBody
  },
})

if (!floorInfo.value || floorInfo.value === 404) {
  showError({
    statusCode: 404,
    statusMessage: '页面未找到',
  })
}

const tabActive = computed(() => route.meta.tabActive || 0)

/**
 * 获取tab控制数据
 */
const { data: tabShowControl } = useAsyncData(() => getFloorTabSwitch({ cityId: city, floorId: route.params.floorId }), {
  transform(res) {
    const { code, dataBody = {} as TabDataType } = res
    return code === 0 ? dataBody : {}
  },
})

provide('tabShowControl', tabShowControl)

watch(() => route.params.floorId, () => {
  if (route.params.floorId) {
    floorInfoRefresh()
  }
})
provide('floorInfo', floorInfo)// 置业顾问
const adviserList = computed(() => {
  if (floorInfoTwo.value?.adviserList)
    return floorInfoTwo.value.adviserList
  else
    return []
})

/**
 * 获取报名配置
 */
const { data: configMap } = await useAsyncData(() => getSignUpConfig({
  configCodes: ['pcPropertyConsultantHome', 'pcApplyGroup'].join(','),
}), {
  transform: (res: any) => {
    if (res.code === 0) {
      return res?.dataBody?.reduce((acc: any, item: any) => {
        acc[item.configCode] = item
        return acc
      }, {})
    }
    else {
      return {}
    }
  },
})
provide('configMap', configMap)
/**
 * 获取广告数据
 */
const { data: adData } = await useAsyncData(() => getAdList({ siteId: city, positionId: 26560 }), {
  transform(res) {
    const { code, dataBody = [] as any[] } = res
    return code === 0 ? dataBody?.list[0] : {}
  },
})
onMounted(() => {
  //  是否已关注，客户端二次校验
  userSaveCheck({
    numberId: [floorInfo.value.floorId],
    sortId: 5,
    cb: (res) => {
      const { code, dataBody } = res
      if (code === 0) {
        floorInfo.value.isFavorite = dataBody[floorInfo.value.floorId]
      }
    },
  })
})
</script>

<template>
  <!--    广告位 -->
  <div v-if="adData" class="ad-box" :style="`height: ${adData?.height}px`">
    <div class="advertisement" :style="`width: ${adData?.width}px;height: ${adData?.height}px`">
      <a :href="adData?.url" target="_blank">
        <img :src="adData?.pic" :alt="adData?.url">
      </a>
      <div v-if="adData?.sellNum" class="certificate">
        {{ adData?.sellNum }}
      </div>
      <div class="adTag">
        广告
      </div>
    </div>
  </div>
  <!--  顶部菜单 -->
  <ModuleSwitching />
  <!--  搜索框 -->
  <SearchTop />
  <div style="width: 100%;border-bottom: 1px solid #DEDEDE" />

  <Container>
    <!--    楼栋信息 -->
    <floor-phone :floor-info="floorInfo" />
  </Container>

  <!--    菜单 -->
  <tab-menu-revise :tab-show-control="tabShowControl" use-key is-sticky :floor-info="floorInfo" :active="tabActive" class="mt-10px mb-40px" :jump-away="true" />

  <Container>
    <!--    页面 -->
    <NuxtPage :floor-info="floorInfo" />
    <!--    周边楼盘 -->
    <div v-show="floorInfo?.cooperate !== 1" class="mb-25px mt-25px">
      <MaybeLike title="周边楼盘" :data-list="roundData" />
    </div>
    <!--    面包屑 -->
    <Breadcrumb :tdk-data="tdkData" />
    <!--    猜你喜欢 -->
    <MaybeLike :data-list="floorMayLikeData" />
    <!--    底部footer -->
    <Footer :tdk-data="tdkData" />
  </Container>
  <!--  侧边工具 -->
  <ToolButton
    :is-favorite="floorInfo?.isFavorite"
    :number-id="floorInfo?.floorId"
    :adviser-list="adviserList"
    config-id="pcPropertyConsultantHome"
    apply-group-config-id="pcApplyGroup"
    @like-change="(val: number) => {
      floorInfo.isFavorite = val;
    }"
  />
</template>

<style scoped lang="scss">
.ad-box {
  width: 100%;
  height: 140px;
  position: relative;
  overflow: hidden;

  .certificate {
    position: absolute;
    bottom: 15px;
    right: 15px;
    padding: 4px 8px;
    background: #00000033;
    box-sizing: border-box;
    color: #FFFFFF80;
    font-size: 14px;
    text-align: center;
  }

  .adTag {
    position: absolute;
    bottom: 0;
    left: 0;
    padding: 1px 3px;
    //width: 21px;
    //height: 11px;
    box-sizing: border-box;
    border: 0.5px solid #FFFFFF7F;
    color: #FFFFFF;
    font-size: 8px;
    text-align: center;
  }

  .advertisement {
    width: 2000px;
    height: 140px;
    background: #1BB440;
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);

    img {
      width: 100%;
    }
  }
}
</style>
